"use client"

import type { DethatchingResult } from "@/lib/dethatching-calculator"

/**
 * Dethatching-window timeline (Claude Design DethatchingPage, adapted).
 *
 * A ROLLING 12-month horizon anchored to the start of the current month, NOT a
 * fixed Jan-Dec calendar. This matters because the engine rolls a window that
 * has already ended forward into next year: on a fixed calendar that next-year
 * bar would render to the LEFT of the TODAY marker (reading as "in the past")
 * even though the countdown says it is months away. Positioning every date by
 * its real, year-aware distance from now keeps the strip chronological: left
 * edge = now, right edge = now + 12 months, everything shaded is upcoming.
 */

const MONTH_LETTERS = "JFMAMJJASOND".split("")

function fmtShort(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export function MonthWindowStrip({ result }: { result: DethatchingResult }) {
  const wall = new Date()
  // Day-granular "now": the marker position must be identical on the server and
  // the hydrating client, and millisecond `new Date()` differs between the two
  // renders (a hydration mismatch on the TODAY marker's left%). Truncating to
  // the start of today is stable across the hydration boundary.
  const now = new Date(wall.getFullYear(), wall.getMonth(), wall.getDate())
  // Anchor the horizon to the first of the current month so the 12 month cells
  // line up with real month boundaries on the same scale as the shaded spans.
  const origin = new Date(now.getFullYear(), now.getMonth(), 1)
  const horizonEnd = new Date(now.getFullYear(), now.getMonth() + 12, 1)
  const span = horizonEnd.getTime() - origin.getTime()

  const pct = (d: Date): number => {
    const raw = ((d.getTime() - origin.getTime()) / span) * 100
    return Math.max(0, Math.min(100, raw))
  }

  const isWarm = result.season === "Warm"
  const inWindow = result.status === "optimal" || result.status === "acceptable"

  // A window can start before "now" (we are mid-window) — pct() clamps its
  // start to 0 so it reads as open-at-the-left. Skip any window that has fully
  // passed relative to the horizon (defensive; the engine rolls forward).
  const spans: { left: number; width: number; color: string }[] = []
  const pushSpan = (start: Date, end: Date, color: string) => {
    if (end.getTime() <= origin.getTime()) return
    const left = pct(start)
    const width = Math.max(2.5, pct(end) - left)
    spans.push({ left, width, color })
  }
  pushSpan(result.primaryWindow.start, result.primaryWindow.end, "#16a34a")
  if (result.secondaryWindow) {
    pushSpan(result.secondaryWindow.start, result.secondaryWindow.end, "#86efac")
  }

  // 12 month cells starting at the current month, positioned on the pct scale.
  const cells = Array.from({ length: 12 }, (_, i) => {
    const cellStart = new Date(now.getFullYear(), now.getMonth() + i, 1)
    const cellEnd = new Date(now.getFullYear(), now.getMonth() + i + 1, 1)
    return {
      letter: MONTH_LETTERS[(now.getMonth() + i) % 12],
      left: pct(cellStart),
      width: pct(cellEnd) - pct(cellStart),
      isCurrent: i === 0,
    }
  })

  let countdown: string
  let countdownColor: string
  if (inWindow) {
    countdown = `Window open now${result.daysRemaining !== undefined ? `, ${result.daysRemaining} days left` : ""}`
    countdownColor = "#15803d"
  } else {
    countdown = `${result.daysRemaining ?? ""} days until your window opens (${fmtShort(result.recommendedWindow.start)})`
    countdownColor = result.status === "approaching" ? "#92400e" : "#374151"
  }

  const legend: { color: string; label: string }[] = [
    { color: "#16a34a", label: isWarm ? "Your window (peak summer growth)" : "Primary window (fall)" },
  ]
  if (result.secondaryWindow) legend.push({ color: "#86efac", label: "Backup window (spring)" })

  return (
    <div className="mt-7 border-t border-gray-100 pt-5">
      <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
          When to do it · next 12 months
        </span>
        <span className="text-[13px] font-bold" style={{ color: countdownColor }}>
          {countdown}
        </span>
      </div>
      <div className="relative pt-[18px]">
        <div className="absolute left-0 right-0 top-0 h-3.5">
          {cells.map((c, i) => (
            <span
              key={i}
              className="absolute text-center text-[11px] font-semibold"
              style={{
                left: `${c.left}%`,
                width: `${c.width}%`,
                color: c.isCurrent ? "#0f1d14" : "#9ca3af",
              }}
            >
              {c.letter}
            </span>
          ))}
        </div>
        <div className="relative h-4 overflow-hidden rounded-full bg-gray-100">
          {spans.map((w, i) => (
            <div
              key={i}
              className="absolute bottom-0 top-0 rounded-full"
              style={{ left: `${w.left}%`, width: `${w.width}%`, background: w.color, transition: "all 400ms ease" }}
            />
          ))}
        </div>
        <div
          className="absolute w-0.5 rounded-[1px] bg-[#0f1d14]"
          style={{ top: 14, bottom: -22, left: `${pct(now)}%`, transition: "left 400ms ease" }}
        >
          <div className="absolute -top-1 -left-[3px] h-2 w-2 rounded-full bg-[#0f1d14]" />
          <div className="absolute -bottom-0.5 left-0 whitespace-nowrap text-[10px] font-bold tracking-wide text-[#0f1d14]">
            TODAY
          </div>
        </div>
      </div>
      <div className="mt-[30px] flex flex-wrap gap-4">
        {legend.map((l, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: l.color }} />
            {l.label}
          </span>
        ))}
      </div>
    </div>
  )
}
